/**
 * Contractor Messaging Constants (P4)
 *
 * Single source of truth for ALL P4 contractor-experience copy: status
 * panel labels, opportunity banners, action toasts, interest inline
 * notices, dispatcher labels, and empty states.
 *
 * Locked rule: NO inline string literals for these surfaces. Components
 * import from this module so copy can be audited (tone / legal / brand)
 * in one place.
 *
 * Plain language; conversational; no percentages or formulas; no
 * gamification / leaderboards / pressure copy. No financial leakage —
 * never references contractValue, margin, or internal pricing.
 */

import {
  getReliabilityTierLabel,
  getReliabilityTierDescription,
} from "./offerMetrics";
import type { ReliabilityTier } from "@/data/contractorReliability";

/* ── Status panel ─────────────────────────────────────────────────── */

export const STATUS_PANEL = {
  heading: "Your Status",
  tierExplain:
    "This determines how often you receive and see opportunities.",
  metrics: {
    acceptance: "Acceptance",
    responseTime: "Response Time",
    expiry: "Expiry",
    postAcceptReliability: "Post-Accept Reliability",
  },
  stillLearning:
    "We're still learning your performance — complete a few offers to unlock more visibility.",
  noTier: "Reliability hasn't been calculated yet.",
} as const;

export const HOW_TO_GET_MORE_WORK = {
  heading: "How to Get More Work",
  bullets: [
    "Accept offers you can fully commit to.",
    "Respond to offers quickly — slow responses cost the customer time.",
    "Don't let offers expire without a response.",
    "Complete the jobs you accept all the way through.",
  ],
} as const;

/* ── Opportunity visibility banners (per-mode) ────────────────────── */

export const OPPORTUNITY_BANNER = {
  limited: {
    title: "Limited opportunity browsing",
    body:
      "You're seeing a limited set of opportunities based on your current performance and coverage. Requesting an offer doesn't guarantee assignment — your dispatcher reviews every request before sending an offer.",
  },
  offerOnly: {
    title: "Offers only",
    body: "You'll receive offers directly when work is available for you.",
  },
  none: {
    title: "Browsing paused",
    body: "Complete assigned work to regain access to opportunities.",
  },
} as const;

export const OPPORTUNITY_EMPTY_LIMITED =
  "No opportunities available right now — keep your status strong to see more.";

/* ── Offer feedback toasts (fired from useMarketplaceOffers) ─────── */

export const OFFER_TOAST = {
  accepted: {
    title: "You're now assigned",
    description: "Please follow the job requirements carefully.",
  },
  declined: {
    title: "Response recorded",
    description: "Your response helps improve future job matching.",
  },
  expired: {
    title: "Offer expired",
    description: "Missed responses may reduce future opportunities.",
  },
} as const;

/* ── Interest feedback toasts (fired from useContractorInterest) ── */

export const INTEREST_TOAST = {
  submitted: {
    title: "Request sent",
    description: "Your dispatcher will review it.",
  },
  duplicate: {
    title: "Already requested",
    description: "You've already requested this job.",
  },
} as const;

/* ── Closed-interest INLINE notices (passive, no toast) ──────────── */

export const INTEREST_INLINE_NOTICE = {
  offer_sent: "An offer has been sent to you.",
  dispatcher_dismissed: "This request was not selected.",
  job_left_dispatch: "This job is no longer available.",
} as const;

/* ── Dispatcher labels (light context only — no raw metrics) ─────── */

export const DISPATCHER_LABEL = {
  highReliability: "High reliability contractor",
  newContractor: "New contractor (low data)",
  frequentExpirations: "Frequent expirations",
} as const;

/* ── Archive / history empty states ──────────────────────────────── */

export const OFFER_HISTORY_EMPTY =
  "You'll see your offer activity here once you start receiving jobs.";

/* ── Re-export tier resolver for convenient single-import ────────── */
export { getReliabilityTierLabel, getReliabilityTierDescription };
export type { ReliabilityTier };

/* ── P5: Onboarding flow copy ────────────────────────────────────── */

export const ONBOARDING_STEPS = [
  {
    key: "how_work_flows",
    title: "How work flows on Koredor",
    body: "Jobs aren't publicly claimed. You receive curated offers based on your fit and track record. Visibility is earned over time — the stronger your performance, the more you see.",
  },
  {
    key: "how_to_get_more",
    title: "How you get more work",
    body: "Accept jobs you can fully commit to. Respond to offers quickly. Don't let offers expire. Complete the work you accept. That's the entire formula.",
  },
  {
    key: "what_to_expect",
    title: "What to expect",
    body: "You won't see every job. Requesting an offer doesn't guarantee assignment — your dispatcher reviews every request and decides where work goes.",
  },
  {
    key: "ready",
    title: "You're ready",
    body: "Your portal is set up. Head to your dashboard to see your status and any opportunities available to you.",
  },
] as const;

export const ONBOARDING_MODAL_COPY = {
  skip: "Skip for now",
  next: "Next",
  back: "Back",
  finish: "Got it",
  cta: "Go to your dashboard",
  persistentLink: "How Koredor Works",
  dialogTitle: "Welcome to Koredor",
} as const;

/* ── P5: Expectation framing (placed near, not inside, status card) */

export const EXPECTATION_FRAMING = {
  curated: "Opportunities are curated based on performance.",
  matching: "Your activity helps improve future matching.",
} as const;

/* ── P5: Forbidden contractor-facing language ────────────────────── *
 * Marketplace / open-board framing must never reach contractor UI.
 * Enforced by marketplaceContractorOnboardingP5.test.ts which rg-scans
 * src/components/contractor and src/pages/ContractorPortal.tsx.
 * The test intentionally excludes this constant definition itself.
 */
// FORBIDDEN_LANGUAGE_LIST_START
export const FORBIDDEN_CONTRACTOR_LANGUAGE: readonly string[] = [
  "job board",
  "available jobs",
  "claim a job",
  "bid on",
  "browse all jobs",
  // ── Phase 1b — Network Execution: never expose internal scoring/discipline
  //    vocabulary in contractor-facing surfaces. UI says "in good standing"
  //    or "needs attention" — never the raw mechanics.
  "capped",
  "score limit",
  "penalty",
  "probation",
] as const;
// FORBIDDEN_LANGUAGE_LIST_END

/* ── P6: Dispatch Intelligence (dispatcher-only) ─────────────────── *
 * Resolver returns keys; UI maps via these tables. Plain language.
 * Never reveals customer revenue or internal pricing.
 */

export const DISPATCH_TIER_COPY = {
  strong: { label: "Strong fit", tone: "positive" as const },
  acceptable: { label: "Acceptable", tone: "neutral" as const },
  caution: { label: "Caution", tone: "warning" as const },
  not_recommended: { label: "Not recommended", tone: "negative" as const },
} as const;

export const DISPATCH_CONFIDENCE_COPY = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
} as const;

export const DISPATCH_REASON_COPY = {
  ineligible: "Currently ineligible",
  tier_risk: "Reliability is at risk",
  tier_watch: "Reliability needs monitoring",
  tier_elite_or_reliable: "Strong reliability track record",
  tier_unrated: "Not enough history yet",
  out_of_coverage: "Outside service area",
  in_coverage: "In service area",
  low_sample_size: "Limited offer history",
  frequent_expirations: "Frequently lets offers expire",
  post_accept_reassignment: "Has been reassigned after accepting",
  active_contractor: "Active contractor",
} as const;

export const DISPATCH_RISK_FLAG_COPY = {
  ineligible: "Ineligible",
  out_of_coverage: "Out of coverage",
  frequent_expirations: "Frequent expirations",
  post_accept_reassignment: "Post-accept reassignment",
  low_sample_size: "Low data",
  tier_risk: "Risk tier",
  tier_watch: "Watch tier",
} as const;

export const DISPATCH_OVERRIDE_COPY = {
  warningTitle: "Manual override required",
  warningBody:
    "This contractor isn't recommended for this job. Sending an offer requires your explicit confirmation.",
  confirmCta: "Send offer anyway",
} as const;

/* ── P7: Explainability & Decision Transparency ──────────────────── *
 * Plain-language explanations for every dispatch reason and risk flag.
 * Resolver returns keys; UI renders these strings. No formulas, no
 * scoring vocabulary, no numeric thresholds. Enforced by
 * marketplaceDispatchExplainabilityP7.test.ts (key parity + language
 * guard + no contractValue leakage).
 */

export const DISPATCH_REASON_EXPLANATIONS: Record<
  keyof typeof DISPATCH_REASON_COPY,
  string
> = {
  ineligible:
    "This contractor doesn't currently meet the requirements for this job — coverage, compliance, or pay profile.",
  tier_risk:
    "Recent reliability has slipped. Use caution and confirm the contractor can deliver before assigning.",
  tier_watch:
    "Reliability needs monitoring. Performance is mixed and trending down.",
  tier_elite_or_reliable:
    "This contractor has a consistent track record of accepting and completing offers reliably.",
  tier_unrated:
    "Not enough offer history yet to gauge reliability either way.",
  out_of_coverage:
    "The job site sits outside this contractor's usual coverage area.",
  in_coverage:
    "The job site is inside this contractor's usual coverage area.",
  low_sample_size:
    "Limited offer history means this read on the contractor is less certain.",
  frequent_expirations:
    "This contractor has let offers expire without responding more often than expected.",
  post_accept_reassignment:
    "This contractor has been reassigned after accepting in the past, which can disrupt scheduling.",
  active_contractor:
    "Currently active and available to receive work.",
};

export const DISPATCH_RISK_FLAG_EXPLANATIONS: Record<
  keyof typeof DISPATCH_RISK_FLAG_COPY,
  string
> = {
  ineligible:
    "Eligibility check did not pass. The contractor cannot currently be assigned.",
  out_of_coverage:
    "Job is outside the contractor's normal coverage area.",
  frequent_expirations:
    "Pattern of letting offers expire instead of responding.",
  post_accept_reassignment:
    "History of needing to be reassigned after accepting an offer.",
  low_sample_size:
    "Too little offer history to draw a confident read.",
  tier_risk:
    "Reliability is currently flagged as at-risk.",
  tier_watch:
    "Reliability is currently flagged as needing monitoring.",
};

export const DISPATCH_AUDIT_COPY = {
  trigger: "Why this recommendation?",
  tierLabel: "Recommendation",
  confidenceLabel: "Confidence",
  sampleLabel: "Offer history",
  eligibilityLabel: "Eligibility",
  coverageLabel: "Coverage",
  reasonsLabel: "Why",
  flagsLabel: "Things to watch",
  eligiblePass: "Passed",
  eligibleFail: "Did not pass",
  coverageIn: "In coverage",
  coverageOut: "Out of coverage",
  noFlags: "None",
} as const;

export const DISPATCH_COMPARE_COPY = {
  trigger: "Compare selected",
  drawerTitle: "Compare contractors",
  drawerBody:
    "Side-by-side view of the dispatch read for the contractors you selected. This is a comparison only — it does not change rankings or pick a winner.",
  selectedSummary: (n: number, max: number) =>
    `${n} of ${max} selected`,
  empty: "Select up to 3 contractors using the checkboxes to compare them.",
  cap: 3,
  columnHeadings: {
    tier: "Recommendation",
    confidence: "Confidence",
    reasons: "Top reasons",
    flags: "Things to watch",
  },
} as const;

/* ── P7: Forbidden language inside explanation copy ──────────────── *
 * Explanations must stay plain and non-gamified. The list below is
 * scanned against DISPATCH_REASON_EXPLANATIONS + DISPATCH_RISK_FLAG_
 * EXPLANATIONS by the P7 test suite.
 *
 * Word-boundary regex — substrings inside larger words ("scoreboard"
 * is not flagged; "score" is). Numeric thresholds are caught by a
 * separate digit scan in the test.
 */
// FORBIDDEN_EXPLANATION_LIST_START
export const FORBIDDEN_EXPLANATION_TERMS: readonly string[] = [
  "score",
  "algorithm",
  "ranking",
  "ranked",
  "best",
  "worst",
  "top pick",
  "winner",
  "loser",
  "leaderboard",
] as const;
// FORBIDDEN_EXPLANATION_LIST_END
